import { IS_DEV_ENV } from "constants/app.constants";
import { EvaChart } from "constants/charts.enums";

import {
  DEFAULT_CHART_PRODUCT,
  DEFAULT_MIDSTREAM_CHART_PRODUCT
} from "../../../constants";
import { ChartSettings } from "../../../hooks/charts";
import { EvaChartFeatures } from "../../../types";
import { UNDEFINED_LOCKS } from "../chart";
import {
  DEFAULT_AVERAGE_FEATURE_ENTITY,
  DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
  DEFAULT_CHART_FOCUS_ENTITY,
  DEFAULT_CHART_TYPE_PARAM_FEATURE,
  DEFAULT_CUT_OFF_FEATURE_ENTITY,
  DEFAULT_DATA_TABLE_FEATURE_ENTITY,
  DEFAULT_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
  DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
  DEFAULT_LASSO_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LOG_AXIS_FEATURE_ENTITY,
  DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
  DEFAULT_NORMALIZATION_ENTITY,
  DEFAULT_P10_FEATURE_ENTITY,
  DEFAULT_P50_FEATURE_ENTITY,
  DEFAULT_P90_FEATURE_ENTITY,
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
  DEFAULT_POINT_SIZE_FEATURE_ENTITY,
  DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
  DEFAULT_SCREENSHOT_FEATURE_ENTITY,
  DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
  DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
  DEFAULT_SOURCE_FEATURE_ENTITY,
  DEFAULT_TIME_STEP_FEATURE_ENTITY
} from "./features";

export const BASE_CHART_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: {
    ...DEFAULT_SCREENSHOT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  fullScreen: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  datatable: {
    ...DEFAULT_DATA_TABLE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  spaghetti: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  forecast: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  source: {
    ...DEFAULT_SOURCE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  debug: {
    ...DEFAULT_FEATURE_ENTITY,
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  },
  timeStep: {
    ...DEFAULT_TIME_STEP_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  isProducingRateType: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  cutoff: {
    ...DEFAULT_CUT_OFF_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  shutInMonths: {
    ...DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  movingAverageDays: {
    ...DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  survivorBias: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  useWeightedRatioAverage: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  lockUnits: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  lasso: {
    ...DEFAULT_LASSO_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  typewells: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  p10: {
    ...DEFAULT_P10_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  p50: {
    ...DEFAULT_P50_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  p90: {
    ...DEFAULT_P90_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  average: {
    ...DEFAULT_AVERAGE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  normalization: {
    ...DEFAULT_NORMALIZATION_ENTITY,
    show: true,
    enabled: true
  },
  chartFocus: {
    ...DEFAULT_CHART_FOCUS_ENTITY,
    show: true,
    enabled: true
  },
  annotations: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  legend: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  chartTypeParam: {
    ...DEFAULT_CHART_TYPE_PARAM_FEATURE,
    show: true,
    enabled: true
  },
  histogramToggle: {
    ...DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  jitterPlotToggle: {
    ...DEFAULT_JITTER_PLOT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  quarterSlope: {
    ...DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    enabled: true,
    show: true
  },
  unitSlope: {
    ...DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    enabled: true,
    show: true
  },
  halfSlope: {
    ...DEFAULT_SLOPE_LINE_FEATURE_ENTITY,
    enabled: true,
    show: true
  },
  calculationMethod: {
    ...DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  pressureChartSettings: {
    ...DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY,
    show: true,
    enabled: true
  },
  logAxis: {
    ...DEFAULT_LOG_AXIS_FEATURE_ENTITY,
    properties: {
      x: { active: false },
      y: { active: false }
    }
  },
  useProbitLineOfBestFitData: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  useLineOfBestFit: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  useSeriesStyleOverride: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: false
  }
};

export const BASE_MIDSTREAM_CHART_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: {
    ...DEFAULT_SCREENSHOT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  fullScreen: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  datatable: {
    ...DEFAULT_DATA_TABLE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  spaghetti: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  debug: {
    ...DEFAULT_FEATURE_ENTITY,
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  },
  cutoff: {
    ...DEFAULT_CUT_OFF_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  shutInMonths: {
    ...DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  movingAverageDays: {
    ...DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  survivorBias: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  useWeightedRatioAverage: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true,
    active: true
  },
  lockUnits: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  lasso: {
    ...DEFAULT_LASSO_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  p10: {
    ...DEFAULT_P10_FEATURE_ENTITY,
    show: true,
    enabled: false
  },
  p50: {
    ...DEFAULT_P50_FEATURE_ENTITY,
    show: true,
    enabled: false
  },
  p90: {
    ...DEFAULT_P90_FEATURE_ENTITY,
    show: true,
    enabled: false
  },
  average: {
    ...DEFAULT_AVERAGE_FEATURE_ENTITY,
    show: true,
    enabled: false
  },
  chartFocus: {
    ...DEFAULT_CHART_FOCUS_ENTITY,
    show: true,
    enabled: true
  },
  legend: {
    ...DEFAULT_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  pointOpacity: {
    ...DEFAULT_POINT_OPACITY_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  pointSize: {
    ...DEFAULT_POINT_SIZE_FEATURE_ENTITY,
    show: true,
    enabled: true
  },
  chartTypeParam: {
    ...DEFAULT_CHART_TYPE_PARAM_FEATURE,
    show: true,
    enabled: true
  },
  logAxis: {
    ...DEFAULT_LOG_AXIS_FEATURE_ENTITY,
    properties: {
      x: { active: false },
      y: { active: false }
    }
  }
};

export const BASE_CHART_SETTINGS: ChartSettings = {
  chartType: EvaChart.RateCum,
  features: BASE_CHART_FEATURES,
  locks: UNDEFINED_LOCKS,
  product: DEFAULT_CHART_PRODUCT
};

export const BASE_MIDSTREAM_CHART_SETTINGS: ChartSettings = {
  chartType: EvaChart.RateDateMidstream,
  features: BASE_MIDSTREAM_CHART_FEATURES,
  locks: UNDEFINED_LOCKS,
  product: DEFAULT_MIDSTREAM_CHART_PRODUCT
};
